import React, { useState, useEffect } from 'react'
import {
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Search as SearchIcon, Clear as ClearIcon } from '@material-ui/icons'
import { debounce } from 'lodash'
import { colors } from '../../../../Themes/Colors'
import {
  SearchContainer,
  SearchFieldsWrapper,
  SearchField,
  ActiveFiltersWrapper,
  FilterChip,
} from '../AdminComponents/AdminSearchBar.styled'
import {
  SelectFormControl,
  SelectAdornment,
  FilterLabelContainer,
  FilterLabel,
  ClearAllChip,
} from './JobSearchBar.styled'
import { SearchFilters } from '../AdminComponents'

export interface SearchField {
  key: string
  label: string
  placeholder?: string
  type?: 'text' | 'number' | 'select'
  options?: { value: string; label: string }[]
}

interface JobSearchBarProps {
  fields: SearchField[]
  onSearch: (filters: SearchFilters) => void
  debounceMs?: number
  initialFilters?: SearchFilters
}

export const JobSearchBar: React.FC<JobSearchBarProps> = ({
  fields,
  onSearch,
  debounceMs = 300,
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<SearchFilters>(initialFilters)

  const debouncedOnSearch = React.useMemo(
    () => debounce(onSearch, debounceMs),
    [onSearch, debounceMs]
  )

  useEffect(() => {
    debouncedOnSearch(filters)
  }, [filters, debouncedOnSearch])

  const handleFieldChange = (field: string, value: string) => {
    setFilters((prev) => {
      if (value.trim() === '') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [field]: removed, ...rest } = prev
        return rest
      }
      return { ...prev, [field]: value }
    })
  }

  const clearFilter = (field: string) => {
    handleFieldChange(field, '')
  }

  const clearAllFilters = () => {
    setFilters({})
  }

  const activeFilterCount = Object.keys(filters).length

  return (
    <SearchContainer>
      <SearchFieldsWrapper>
        {fields.map((field) => {
          if (field.type === 'select' && field.options) {
            return (
              <SelectFormControl
                key={field.key}
                variant='outlined'
                size='small'
              >
                <InputLabel>{field.label}</InputLabel>
                <Select
                  label={field.label}
                  value={filters[field.key] || ''}
                  onChange={(e) =>
                    handleFieldChange(field.key, `${e.target.value}`)
                  }
                  startAdornment={
                    <InputAdornment position='start'>
                      <SelectAdornment>
                        <SearchIcon
                          style={{ color: colors.mediumGrey, fontSize: 20 }}
                        />
                      </SelectAdornment>
                    </InputAdornment>
                  }
                >
                  <MenuItem value=''>
                    <em>All</em>
                  </MenuItem>
                  {field.options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </SelectFormControl>
            )
          }

          return (
            <SearchField
              key={field.key}
              label={field.label}
              placeholder={
                field.placeholder || `Search by ${field.label.toLowerCase()}`
              }
              variant='outlined'
              size='small'
              type={field.type || 'text'}
              value={filters[field.key] || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    style={{ marginRight: 8, color: colors.mediumGrey }}
                  />
                ),
                endAdornment: filters[field.key] && (
                  <IconButton
                    size='small'
                    onClick={() => clearFilter(field.key)}
                    style={{ padding: 4 }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                ),
              }}
            />
          )
        })}
      </SearchFieldsWrapper>

      {activeFilterCount > 0 && (
        <ActiveFiltersWrapper>
          <FilterLabelContainer>
            <FilterLabel variant='body2'>
              Active filters ({activeFilterCount}):
            </FilterLabel>
          </FilterLabelContainer>
          {Object.entries(filters).map(([key, value]) => {
            const field = fields.find((f) => f.key === key)
            const displayValue =
              field?.type === 'select'
                ? field.options?.find((opt) => opt.value === value)?.label ||
                  value
                : value
            return (
              <FilterChip
                key={key}
                label={`${field?.label}: ${displayValue}`}
                onDelete={() => clearFilter(key)}
                size='small'
              />
            )
          })}
          <ClearAllChip
            label='Clear all'
            onClick={clearAllFilters}
            size='small'
            variant='outlined'
          />
        </ActiveFiltersWrapper>
      )}
    </SearchContainer>
  )
}
